import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const LastUpdated = styled.p`
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
    color: var(--text-color);
  }

  p {
    line-height: 1.8;
    color: var(--gray-dark);
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;
    line-height: 1.8;
    color: var(--gray-dark);
  }

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: var(--text-color);
  }
`;

const ContactBox = styled.div`
  background-color: var(--gray-light);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const TermsAndConditions: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('termsAndConditions.title')}</Title>

      <Section>
        <p>{t('termsAndConditions.intro')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section1.heading')}</h2>
        <p>{t('termsAndConditions.section1.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section2.heading')}</h2>
        <h3>{t('termsAndConditions.section2.sub1Heading')}</h3>
        <p>{t('termsAndConditions.section2.sub1Text')}</p>

        <h3>{t('termsAndConditions.section2.sub2Heading')}</h3>
        <p>{t('termsAndConditions.section2.sub2Text')}</p>

        <h3>{t('termsAndConditions.section2.sub3Heading')}</h3>
        <p>{t('termsAndConditions.section2.sub3Text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section3.heading')}</h2>
        <h3>{t('termsAndConditions.section3.sub1Heading')}</h3>
        <p>{t('termsAndConditions.section3.sub1Text')}</p>

        <h3>{t('termsAndConditions.section3.sub2Heading')}</h3>
        <p>{t('termsAndConditions.section3.sub2Text')}</p>

        <h3>{t('termsAndConditions.section3.sub3Heading')}</h3>
        <p>{t('termsAndConditions.section3.sub3Text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section4.heading')}</h2>
        <h3>{t('termsAndConditions.section4.sub1Heading')}</h3>
        <p>{t('termsAndConditions.section4.sub1Text')}</p>

        <h3>{t('termsAndConditions.section4.sub2Heading')}</h3>
        <p>{t('termsAndConditions.section4.sub2Text')}</p>

        <h3>{t('termsAndConditions.section4.sub3Heading')}</h3>
        <p>{t('termsAndConditions.section4.sub3Text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section5.heading')}</h2>
        <h3>{t('termsAndConditions.section5.sub1Heading')}</h3>
        <p>{t('termsAndConditions.section5.sub1Text')}</p>

        <h3>{t('termsAndConditions.section5.sub2Heading')}</h3>
        <p>{t('termsAndConditions.section5.sub2Text')}</p>

        <h3>{t('termsAndConditions.section5.sub3Heading')}</h3>
        <p>{t('termsAndConditions.section5.sub3Text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section6.heading')}</h2>
        <p>{t('termsAndConditions.section6.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section7.heading')}</h2>
        <p>{t('termsAndConditions.section7.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section8.heading')}</h2>
        <p>{t('termsAndConditions.section8.text1')}</p>
        <p>{t('termsAndConditions.section8.text2')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section9.heading')}</h2>
        <p>{t('termsAndConditions.section9.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section10.heading')}</h2>
        <p>{t('termsAndConditions.section10.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section11.heading')}</h2>
        <p>{t('termsAndConditions.section11.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section12.heading')}</h2>
        <p>{t('termsAndConditions.section12.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section13.heading')}</h2>
        <p>{t('termsAndConditions.section13.text')}</p>
      </Section>

      <Section>
        <h2>{t('termsAndConditions.section14.heading')}</h2>
        <p>{t('termsAndConditions.section14.text')}</p>
      </Section>

      <ContactBox>
        <h3>{t('termsAndConditions.contactBox.heading')}</h3>
        <p><strong>{t('termsAndConditions.contactBox.companyName')}</strong></p>
        <p>{t('termsAndConditions.contactBox.address')}</p>
        <p>{t('termsAndConditions.contactBox.email')}</p>
        <p>{t('termsAndConditions.contactBox.phone')}</p>
        <p>{t('termsAndConditions.contactBox.companyRegistration')}</p>
        <p>{t('termsAndConditions.contactBox.vatNumber')}</p>
      </ContactBox>
    </Container>
  );
};

export default TermsAndConditions;
